import { splitEncoding } from '@contentful/live-preview';
import Grid from '@mui/material/Grid';

import Button, { ButtonVariant } from '@/components/BaseLibrary/Button/Button';
import Eyebrow from '@/components/Eyebrow/Eyebrow';
import Heading from '@/components/Heading/Heading';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import Link from '@/components/Link/Link';
import type { LinkContent } from '@/contentful/contentful-exports';
import useGlobalStyles from '@/styles/global.styles';
import useStyles from './InnerContent.styles';

interface Props {
  eyebrow?: string;
  isOnDark: boolean;
  heading?: string;
  subheading?: string;
  bulletedList?: string[];
  termsAndConditions?: LinkContent;
  termsAndConditionsStatement?: string;
  primaryCta?: LinkContent;
  secondaryCta?: LinkContent;
  horizontalAlignment?: 'Left' | 'Center';
  slideMode?: boolean;
}

export default function InnerContent({
  heading,
  subheading,
  bulletedList,
  termsAndConditions,
  termsAndConditionsStatement,
  primaryCta,
  secondaryCta,
  eyebrow,
  isOnDark,
  horizontalAlignment,
  slideMode,
}: Props) {
  const { classes: globalClasses, cx } = useGlobalStyles({ fixed: false });
  const { classes } = useStyles();
  const isCentered = horizontalAlignment === 'Center';

  const termsAndConditionsText =
    termsAndConditionsStatement && termsAndConditions
      ? `${termsAndConditionsStatement} `
      : termsAndConditionsStatement;

  return (
    <div
      className={cx(classes.flexCol, classes.gapTwo, {
        [classes.onDark]: isOnDark,
        [classes.onLight]: !isOnDark,
      })}
    >
      <div className={cx(classes.flexCol, classes.gapOne)}>
        <GridContainer fixed>
          <Grid
            className={cx(classes.flexCol, classes.gapMinusOne, {
              [classes.centerItems]: isCentered,
            })}
            size={{
              xs: slideMode ? 12 : 10,
              md: slideMode ? 12 : 9,
            }}
            offset={{
              xs: isCentered ? 1 : 0,
              md: isCentered ? 1 : 0,
            }}
          >
            {eyebrow && <Eyebrow text={eyebrow} isOnDark={isOnDark} />}
            {heading && (
              <Heading
                level='h1'
                classNameOverride={cx('primaryTextColor', {
                  [classes.heading]: slideMode,
                  [globalClasses.h1]: !slideMode,
                })}
              >
                {heading}
              </Heading>
            )}
          </Grid>
        </GridContainer>
        <GridContainer fixed>
          <Grid
            className={cx(classes.flexCol, classes.gapTwo, {
              [classes.centerItems]: isCentered,
            })}
            size={{
              xs: slideMode ? 12 : 10,
              sm: slideMode ? 12 : 6,
              md: slideMode ? 12 : 9,
            }}
            offset={{
              xs: isCentered ? 1 : 0,
              sm: isCentered ? 3 : 0,
              md: isCentered ? 1 : 0,
            }}
          >
            {(subheading || bulletedList || termsAndConditions) && (
              <div>
                {subheading && (
                  <p
                    className={cx('accentTextColor', {
                      [classes.subheading]: slideMode,
                      [globalClasses.bodyLg]: !slideMode,
                    })}
                  >
                    {subheading}
                  </p>
                )}
                {bulletedList && (
                  <ul
                    className={cx(
                      classes.bulletedList,
                      globalClasses.body,
                      'accentTextColor'
                    )}
                  >
                    {bulletedList.map((item, index) => {
                      const { cleaned } = splitEncoding(item);

                      return (
                        <li
                          key={`${cleaned}-${
                            // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                            index
                          }`}
                        >
                          {cleaned}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {(termsAndConditions || termsAndConditionsStatement) && (
                  <span
                    className={cx(
                      globalClasses.bodyHyperlink,
                      classes.termsAndConditionsText,
                      'accentTextColor'
                    )}
                  >
                    {termsAndConditionsText}
                    {termsAndConditions && (
                      <Link
                        className={cx(globalClasses.bodyHyperlink)}
                        href={termsAndConditions.fields.url}
                      >
                        {termsAndConditions.fields.text}
                      </Link>
                    )}
                  </span>
                )}
              </div>
            )}
          </Grid>
        </GridContainer>
      </div>
      {primaryCta && (
        <GridContainer fixed>
          <Grid
            className={cx(classes.ctaContainer, {
              [classes.centerItems]: isCentered,
            })}
            size={12}
          >
            <Button
              isOnDark={isOnDark}
              variant={ButtonVariant.PRIMARY}
              label={primaryCta.fields.text}
              href={primaryCta.fields.url}
              videoUrl={primaryCta.fields.videoUrl}
            />
            {secondaryCta && (
              <Button
                isOnDark={isOnDark}
                variant={ButtonVariant.SECONDARY}
                label={secondaryCta.fields.text}
                href={secondaryCta.fields.url}
              />
            )}
          </Grid>
        </GridContainer>
      )}
    </div>
  );
}
